/* *
 * api-init.js
 * * Single-import helper for any page that needs API / WebSocket / ChatApp URLs.
 *
 * USAGE
 * *   import { getApiBase, getServerBase, getClientBase, getWsBase, waitForPort }
 *     from '../../js/utils/api-init.js';
 *
 *   // Option A guaranteed port discovery before any fetch:
 *   await waitForPort();
 *   const data = await fetch(getApiBase() + '/analytics/stats');
 *
 *   // Option B fire and forget (uses cached port immediately):
 *   const apiBase = getApiBase();
 *
 * This module is the replacement for every inline
 *   const API_BASE = isDevEnvironment() ? 'http://localhost:5000/api' : '...'
 * pattern. Never create that pattern again.
 *
 * @module AscendDraft-Website/js/utils/api-init */

// PortEngine fully removed — direct hostname detection used throughout.

/* *
 * Wait for PortEngine to finish discovering the active port.
 * Safe to call multiple times only ever initialises once.
 * @returns {Promise<void>} */
export async function waitForPort() {
  // No-op: port discovery no longer needed — all URLs resolved via hostname detection.
  return Promise.resolve();
}

/* *
 * Full API URL including /api suffix.
 * Dev example:  http://localhost:5001/api
 * Prod example: https://api.ascenddraft.com/api
 * @returns {string} */
export function getApiBase() {
  var h = window.location.hostname;
  var isLocal = h === 'localhost' || h === '127.0.0.1' || h === '' || h.startsWith('192.168.') || window.location.protocol === 'file:';
  return isLocal ? 'http://localhost:5001/api' : 'https://api.ascenddraft.com/api';
}

/* *
 * Bare server URL (no /api).
 * Dev example:  http://localhost:5001
 * Prod example: https://api.ascenddraft.com
 * @returns {string} */
export function getServerBase() {
  return getApiBase().replace(/\/api$/, '');
}

/* *
 * Client base URL (static site on Cloudflare Pages).
 * Dev example:  http://localhost:3001
 * Prod example: same origin (ascenddraft.com)
 * @returns {string} */
export function getClientBase() {
  var h = window.location.hostname;
  var isLocal = h === 'localhost' || h === '127.0.0.1' || h === '' || h.startsWith('192.168.') || window.location.protocol === 'file:';
  return isLocal ? 'http://localhost:3001' : window.location.origin;
}

/* *
 * WebSocket server URL.
 * Dev example:  ws://localhost:5001
 * Prod example: wss://api.ascenddraft.com
 * @returns {string} */
export function getWsBase() {
  var api = getApiBase().replace(/^http/, 'ws').replace('/api', '');
  return api;
}

//  ==== Client URL helpers (static site on Cloudflare Pages) —————————————————————
const _CHAT_APP_PROD = window.location.origin;


/* *
 * Client base URL production-aware.
 * Dev:  http://localhost:3001
 * Prod: window.location.origin (ascenddraft.com via Cloudflare)
 * @returns {string} */
export function getChatAppBase() {
  var h = window.location.hostname;
  var isLocal = h === 'localhost' || h === '127.0.0.1' || h === '' || h.startsWith('192.168.') || window.location.protocol === 'file:';
  return isLocal ? 'http://localhost:3001' : _CHAT_APP_PROD;
}

/* *
 * Client API base URL (including /api suffix).
 * @returns {string} */
export function getChatAppApiBase() {
  return getChatAppBase() + '/api';
}

/* *
 * Client WebSocket URL.
 * @returns {string} */
export function getChatAppWsBase() {
  const base = getChatAppBase();
  return base.replace(/^http/, base.startsWith('https') ? 'wss' : 'ws');
}

/* *
 * Build a full authenticated fetch to the API.
 * Reads the access token from localStorage automatically.
 *
 * @param {string} path       - Path relative to /api, e.g. '/analytics/staff/stats'
 * @param {RequestInit} [opts] - Fetch options override
 * @returns {Promise<any>}    - Parsed JSON body (throws on non-OK responses) */
export async function apiFetch(path, opts = {}) {
  const token = localStorage.getItem('ascend_token');
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(opts.headers || {}),
  };
  const res = await fetch(getApiBase() + path, { ...opts, headers });
  if (!res.ok) {
    let body;
    try { body = await res.json(); } catch { body = {}; }
    const msg = body?.message || body?.error || body?.msg || `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return res.json();
}
